import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { useNavigate } from 'react-router-dom';
import { useNotice } from '../components/DialogProvider';
import { useInstance } from '../contexts/InstanceContext';
import MainViewHeader from '../components/MainViewHeader';
import { isStatusHiddenFromUsers } from '../utils/statusTranslations';
import API_ENDPOINTS from '../config/api';
import ModalWrapper from '../components/ModalWrapper';
import { resolveEntryUrl } from '../utils/participantEntry';

const Container = styled.div`
  width: 100%;
  min-height: 100vh;
  background: linear-gradient(180deg,
    #000000 0%,
    #000000 30%,
    #1a0f2e 70%,
    #2d1b69 90%,
    #4a2882 100%
  );
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 104px 40px 80px 40px;
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  width: 100%;
`;

const Title = styled.h1`
  font-size: 48px;
  font-weight: 700;
  color: #fff;
  text-align: center;
  margin: 0 0 16px 0;
  text-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
`;

const Subtitle = styled.p`
  font-size: 20px;
  color: rgba(255, 255, 255, 0.8);
  text-align: center;
  margin: 0 0 64px 0;
  font-weight: 300;
`;

const InstanceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 24px;
  margin-bottom: 40px;
`;

const InstanceCard = styled.div`
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 32px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  min-height: 200px;

  &:hover {
    transform: translateY(-4px);
    background: rgba(255, 255, 255, 0.12);
    border-color: rgba(138, 43, 226, 0.5);
    box-shadow: 0 12px 32px rgba(138, 43, 226, 0.2);
  }

  &:active {
    transform: translateY(-2px);
  }
`;

const InstanceHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
  position: relative;
`;

const InstanceTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #fff;
  margin: 0;
  padding-right: 32px;
`;

const InfoButton = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  transition: all 0.2s ease;
  z-index: 10;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.4);
    color: #fff;
    transform: scale(1.1);
  }
`;

const BadgeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LockIcon = styled.span`
  font-size: 18px;
  opacity: 0.8;
`;

const InstanceBadge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  background: ${props => props.type === 'live' ?
    'rgba(251, 191, 36, 0.2)' :
    'rgba(59, 130, 246, 0.2)'};
  color: ${props => props.type === 'live' ?
    '#fbbf24' :
    '#3b82f6'};
  border: 1px solid ${props => props.type === 'live' ?
    'rgba(251, 191, 36, 0.3)' :
    'rgba(59, 130, 246, 0.3)'};
`;

const InstanceDescription = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  margin: 0 0 16px 0;
  line-height: 1.6;
  flex: 1;
`;

const InstanceMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const MetaItem = styled.div`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 60px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 18px;
`;

const EmptyState = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  padding: 60px;
  text-align: center;
  color: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
`;

const EmptyTitle = styled.h2`
  font-size: 28px;
  font-weight: 600;
  margin: 0 0 16px 0;
  color: #fff;
`;

const EmptyText = styled.p`
  font-size: 16px;
  opacity: 0.8;
  margin: 0;
`;

const ModalContent = styled.div`
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 2px solid rgba(138, 43, 226, 0.3);
  border-radius: 16px;
  padding: 48px;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  position: relative;
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #fff;
  margin: 0 0 8px 0;
  text-align: center;
`;

const ModalSubtitle = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  margin: 0 0 24px 0;
  text-align: center;
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const ModalButton = styled.button`
  flex: 1;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid;

  ${props => props.primary ? `
    background: rgba(138, 43, 226, 0.3);
    border-color: rgba(138, 43, 226, 0.6);
    color: #fff;

    &:hover {
      background: rgba(138, 43, 226, 0.4);
      border-color: rgba(138, 43, 226, 0.8);
    }
  ` : `
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.2);
    color: rgba(255, 255, 255, 0.8);

    &:hover {
      background: rgba(255, 255, 255, 0.1);
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const InfoModalContent = styled(ModalContent)`
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;

  /* Ensure scrolling works on touch devices */
  -webkit-overflow-scrolling: touch;
`;

const InfoSection = styled.div`
  margin-bottom: 24px;
`;

const InfoSectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #fff;
  margin: 0 0 12px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  opacity: 0.9;
`;

const InfoList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const InfoItem = styled.li`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  padding: 8px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  gap: 8px;

  &:last-child {
    border-bottom: none;
  }

  strong {
    color: #fff;
    min-width: 120px;
  }
`;

const ExpandableDocItem = styled.div`
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  &:last-child {
    border-bottom: none;
  }
`;

const DocHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    padding-left: 8px;
    margin-left: -8px;
  }
`;

const DocTitle = styled.div`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ExpandIcon = styled.span`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  transition: transform 0.2s ease;
  ${props => props.expanded && 'transform: rotate(90deg);'}
`;

const DocDetails = styled.div`
  padding: 8px 0 12px 24px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.6;
`;

const DocMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 8px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
`;

const DocTag = styled.span`
  background: rgba(255, 255, 255, 0.1);
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
`;

const InstanceSelectionScreen = () => {
  const navigate = useNavigate();
  const notice = useNotice();
  const { setSelectedInstanceId } = useInstance();
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [selectedInstance, setSelectedInstance] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [expandedDocs, setExpandedDocs] = useState(new Set());

  const truncate = (s, n = 200) => (s && s.length > n ? s.slice(0, n).trimEnd() + '…' : s);

  useEffect(() => {
    const fetchInstances = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_ENDPOINTS.API_ORIGIN()}/api/instances`);
        const data = await response.json();
        // API returns array directly, not wrapped in {instances: [...]}
        // Filter out draft and archived instances from user view
        const filteredData = Array.isArray(data)
          ? data.filter(instance => !isStatusHiddenFromUsers(instance.status))
          : [];
        setInstances(filteredData);
      } catch (error) {
        console.error('Error fetching instances:', error);
        setInstances([]);
      } finally {
        setLoading(false);
      }
    };

    fetchInstances();
  }, []);

  // Logged-in users see only the instances they're allowed to access — visibility
  // IS the gate, no PIN needed. PIN flow is reserved for venue-screen pairing.
  const handleInstanceSelect = async (instance) => {
    setSelectedInstanceId(instance.id);
    try {
      navigate(await resolveEntryUrl(instance.id));
    } catch (err) {
      console.error('Participant entry failed:', err);
      notice.error(err.message);
    }
  };

  const handleInfoClick = async (e, instance) => {
    e.stopPropagation(); // Prevent card click
    setSelectedInstance(instance);
    setShowInfoModal(true);

    // Fetch documents if not already loaded
    if (documents.length === 0) {
      try {
        const response = await fetch(API_ENDPOINTS.RAG_DOCUMENTS());
        const data = await response.json();
        setDocuments(data.documents || []);
      } catch (error) {
        console.error('Error fetching documents:', error);
      }
    }
  };

  const handleInfoModalClose = () => {
    setShowInfoModal(false);
    setSelectedInstance(null);
    setExpandedDocs(new Set());
  };

  const toggleDocExpand = (docId) => {
    setExpandedDocs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(docId)) {
        newSet.delete(docId);
      } else {
        newSet.add(docId);
      }
      return newSet;
    });
  };

  const getDocumentSummary = (doc) => {
    const parts = [];
    if (doc.author) parts.push(`Tekijä: ${doc.author}`);
    if (doc.total_pages) parts.push(`${doc.total_pages} sivua`);
    if (doc.chunk_count) parts.push(`${doc.chunk_count} osiota`);
    return parts.join(' • ');
  };

  if (loading) {
    return (
      <>
        <MainViewHeader titleOverride="Kuutio" instanceTitle="" />
        <Container>
          <LoadingState>Ladataan instansseja...</LoadingState>
        </Container>
      </>
    );
  }

  return (
    <>
      <MainViewHeader titleOverride="Kuutio" instanceTitle="" />
      <Container>
        <ContentWrapper>
          <Title>Valitse Kuutio-instanssi</Title>
          <Subtitle>Valitse työpaja</Subtitle>

          {instances.length === 0 ? (
            <EmptyState>
              <EmptyTitle>Ei saatavilla olevia instansseja</EmptyTitle>
              <EmptyText>
                Järjestelmänvalvoja voi luoda uusia instansseja Admin-näkymässä
              </EmptyText>
            </EmptyState>
          ) : (
            <InstanceGrid>
              {instances.map((instance) => (
                <InstanceCard
                  key={instance.id}
                  onClick={() => handleInstanceSelect(instance)}
                >
                  <InstanceHeader>
                    <InstanceTitle>{instance.title || instance.name}</InstanceTitle>
                    <InfoButton onClick={(e) => handleInfoClick(e, instance)}>
                      ℹ️
                    </InfoButton>
                    <BadgeContainer>
                      {instance.instance_type === 'live' && (
                        <LockIcon>🔒</LockIcon>
                      )}
                      <InstanceBadge type={instance.instance_type}>
                        {instance.instance_type === 'live' ? '🎪 Live' : '💻 Online'}
                      </InstanceBadge>
                    </BadgeContainer>
                  </InstanceHeader>

                  {instance.description && (
                    <InstanceDescription>{truncate(instance.description)}</InstanceDescription>
                  )}

                  <InstanceMeta>
                    {instance.created_at && (
                      <MetaItem>
                        📅 Luotu: {new Date(instance.created_at).toLocaleDateString('fi-FI')}
                      </MetaItem>
                    )}
                    {instance.metadata?.participants && (
                      <MetaItem>
                        👥 Osallistujat: {instance.metadata.participants}
                      </MetaItem>
                    )}
                  </InstanceMeta>
                </InstanceCard>
              ))}
            </InstanceGrid>
          )}
        </ContentWrapper>
      </Container>

      {showInfoModal && selectedInstance && (
        <ModalWrapper visible={showInfoModal} onClose={handleInfoModalClose}>
          <InfoModalContent>


            <ModalTitle>{selectedInstance.title || selectedInstance.name}</ModalTitle>
            <ModalSubtitle>{selectedInstance.description}</ModalSubtitle>

            <InfoSection>
              <InfoSectionTitle>Perustiedot</InfoSectionTitle>
              <InfoList>
                <InfoItem>
                  <strong>Tyyppi:</strong>
                  <span>{selectedInstance.instance_type === 'live' ? '🎪 Live Workshop' : '💻 Online Workshop'}</span>
                </InfoItem>
                <InfoItem>
                  <strong>Osallistujat:</strong>
                  <span>{selectedInstance.participant_count || 0}</span>
                </InfoItem>
                <InfoItem>
                  <strong>Tila:</strong>
                  <span>{selectedInstance.status === 'pre-kuutio' ? 'Valmisteltavana' : selectedInstance.status}</span>
                </InfoItem>
                {selectedInstance.created_at && (
                  <InfoItem>
                    <strong>Luotu:</strong>
                    <span>{new Date(selectedInstance.created_at).toLocaleDateString('fi-FI')}</span>
                  </InfoItem>
                )}
              </InfoList>
            </InfoSection>

            {selectedInstance.scenarios && selectedInstance.scenarios.length > 0 && (
              <InfoSection>
                <InfoSectionTitle>Skenaariot ({selectedInstance.scenarios.length})</InfoSectionTitle>
                <InfoList>
                  {selectedInstance.scenarios.map((scenario, idx) => (
                    <InfoItem key={idx}>
                      <span>{scenario.icon || '📋'}</span>
                      <span>{scenario.title}</span>
                    </InfoItem>
                  ))}
                </InfoList>
              </InfoSection>
            )}

            <InfoSection>
              <InfoSectionTitle>Saatavilla oleva data ({documents.length} dokumenttia)</InfoSectionTitle>
              <InfoList>
                {documents.length === 0 ? (
                  <InfoItem>
                    <span>⏳</span>
                    <span>Ladataan dokumenttilistaa...</span>
                  </InfoItem>
                ) : (
                  documents.map((doc) => (
                    <ExpandableDocItem key={doc.id}>
                      <DocHeader onClick={() => toggleDocExpand(doc.id)}>
                        <DocTitle>
                          <ExpandIcon expanded={expandedDocs.has(doc.id)}>▶</ExpandIcon>
                          <span>📄</span>
                          <span>{doc.source || doc.filename}</span>
                        </DocTitle>
                      </DocHeader>
                      {expandedDocs.has(doc.id) && (
                        <DocDetails>
                          <div>{getDocumentSummary(doc)}</div>
                          {doc.tags && doc.tags.length > 0 && (
                            <DocMeta>
                              {doc.tags.map((tag, idx) => (
                                <DocTag key={idx}>#{tag}</DocTag>
                              ))}
                            </DocMeta>
                          )}
                        </DocDetails>
                      )}
                    </ExpandableDocItem>
                  ))
                )}
              </InfoList>
            </InfoSection>

            <ModalButtons>
              <ModalButton primary onClick={handleInfoModalClose}>
                Sulje
              </ModalButton>
            </ModalButtons>
          </InfoModalContent>
        </ModalWrapper>
      )}
    </>
  );
};

export default InstanceSelectionScreen;
